import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, Input, Output, EventEmitter, inject } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subscription } from 'rxjs';

import { TrackedEvent, UserCreditsViewModel } from './models';
import {
  AppSettingsService,
  AppStorageService,
  CreditService,
  LogService,
  MembershipService,
  NotificationService,
  ValueService,
  HelpService,
  ListingService,
  UserService
} from './services';
import { NavigationService } from './services/navigation.service';
import { SystemService } from './services/system.service';
import AdminNotification from './models-internal/admin-notification';
import { RouteHelperService } from './services/route-helper.service';
import { NavigationStart, NavigationError, NavigationEnd } from '@angular/router';
import { ExternalOrgService } from './services/external-org.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Word from './models-internal/word';
import { LexicalVariablePipe } from './shared-components/pipes/lexical-variable.pipe';
import { NgClass, AsyncPipe } from '@angular/common';
import { environment } from '@env/environment';
import { CLIENT_CONFIG } from './client-config';
import { MsalService } from '@azure/msal-angular';


@Component({
    selector: 'app-header',
    templateUrl: './app-header.component.html',
    styleUrls: ['./app-header.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, RouterLink, LexicalVariablePipe, AsyncPipe]
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  private readonly _componentName = 'AppHeaderComponent';
  @Output() loginEvent = new EventEmitter<void>();
  @Output() logoutEvent = new EventEmitter<void>();
  private _isExternalOrgHomePage = false;
  private unSubSub$ = new Subject<void>();

  public isLoading = true;
  public isLoggedIn = false; // Reactive property for login state

  

  navToggle = false;
  firstName: string;
  hasListing = false;
  environment = {
    background: '',
    marker: '',
  };

  private readonly clientConfig = inject(CLIENT_CONFIG);
  private isLoggedInSubscription: Subscription;
  private refreshSubscription: Subscription;
  private deletedSubscription: Subscription;
  private addedSubscription: Subscription;
  private adminNotificationSubscription: Subscription;

  private availableCreditSubscription: Subscription;

  userSettings: any;
  credits: UserCreditsViewModel;
  creditsSubscription: Subscription;
  storeUrl: string;

  public giftShopUrl: string = undefined;

  adminStoreUrl: string;

  storeOrdersUrl: string;

  public showRecentMenuItem: boolean = false;
    
  public adminNotifications: AdminNotification[];

  private _activeOrgCode: string;

  public malibuUrl: string;

  /**
   * A single external org that the user belongs to.  If more than one exteral org exists, this property is null
   */
  public externalOrg: any;

  /**
   * User's external org codes
   */
  public externalOrgCodes: string[];

  private userServiceExternalOrganizationCodesSubscription: Subscription;
  private userServiceActiveOrgSubscription: Subscription;
  private routerEventsSub: Subscription;

  /**
   * Is the Ambassador Homepage enabled (this property does not factor in the user's membership to the OAA org)
   */
  public ambassadorHomeEnabled: boolean;

  /**
   * Subscription the ambassadorHomeEnabled appsetting value
   */
  public ambassadorHomeEnabledSubscription: Subscription;

  /**
   * Does the user belong to the OAA organization??
   */
  public isAmbassador: boolean;
  
  public landownerLandingPageMode: boolean = false;

  /**
   * Is verbose logging enabled
   */
  private get verboseLogging(): boolean {
    //return true;
    return this.log.verboseLogging;
  }

  get userProfile() {
    if (this.log.verboseLogging) {
      console.log('AppHeaderComponent: userProfile()', this.userService.profile);
    }

    return this.userService.profile;
  }

  public onOrgClick(exOrgCode: string) {
    if (this.log.verboseLogging) {
      console.log(`${this._componentName}.onOrgClick exOrgCode=`, exOrgCode);
    }

    this.userService.setActiveOrg(exOrgCode);

    this.router.navigate([`/eoh/${exOrgCode}`], { queryParamsHandling: 'preserve' });
  }

  /**
   * Is user a member?
   */
  get isMember() {
    let im = this.membershipService.isPremiumMember === true;

    return im;
  }

  get showSearchBox() {
    return true;
  }

  /**
   * Property that controls the Ambassador menu item
   */
  public get showAmbassadorMenuItem() {
    return this.ambassadorHomeEnabled && this.isAmbassador;
  }

  public get showJoinNowButton() {
    if (!this.isLoggedIn) {
      return true;
    }

    if (this.log.verboseLogging) {
      console.log(`${this._componentName}.showJoinNowButton returns false because no other conditions met.`);
    }
    return false;
  }

  public get showBecomeMemberButton() {
    if (this.isLoggedIn && this.isMember === false && !this.isLoading) {
      return true;
    }

    if (this.log.verboseLogging) {
      console.log(`${this._componentName}.showBecomeMemberButton returns false because no other conditions met.`);
    }

    return false;
  }

  public get showLoginButton() {
    if (!this.isLoggedIn) {
      return true;
    }

    if (this.log.verboseLogging) {
      console.log(`${this._componentName}.showLoginButton returns false because no other conditions met.`);
    }

    return false;
  }

  public onStoreLinkClick(url: string) {
    window.open(url, '_blank').focus();
  }

  constructor(
    readonly router: Router,
    private route: ActivatedRoute,
    readonly changeDetectorRef: ChangeDetectorRef,
    readonly listingService: ListingService,
    readonly userService: UserService,
    readonly navigationService: NavigationService,
    readonly creditService: CreditService,
    private notify: NotificationService,
    readonly log: LogService,
    readonly appSettingsService: AppSettingsService,
    private device: DeviceDetectorService,
    private membershipService: MembershipService,
    private systemService: SystemService,
    private routeHelper: RouteHelperService,
    private valueHelper: ValueService,
    private externalOrgService: ExternalOrgService,
    private helpService: HelpService,
    private storage: AppStorageService,
    private msalService: MsalService
  ) {}

  ngOnInit() {

    //Router subscriber
    this.routerEventsSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        //do something on start activity
      }
      if (event instanceof NavigationError) {
        // Handle error
        console.error(event.error);
      }
      if (event instanceof NavigationEnd) {
        // are we in Landowner Landing Page Mode?
        const landownerLandingPageUrls = ['/landowner/profit','/landowner-postcard','/landowner/refine-estimate'];
        //if (event.urlAfterRedirects.startsWith('/landowner/profit')) {
        if (landownerLandingPageUrls.includes(event.urlAfterRedirects)) {
          this.landownerLandingPageMode = true;
        } else {
          this.landownerLandingPageMode = false;
        }

        //do something on end activity
        this.changeDetectorRef.detectChanges();
      }
    });

    this.ambassadorHomeEnabledSubscription = this.appSettingsService.ambassadorHomeEnabled.subscribe({
      next: (result) => {
        if (this.verboseLogging) {
          console.log(`${this._componentName} subscription result=`, result);
        }

        this.ambassadorHomeEnabled = result;
      },
      error: (err) => {
        console.error(err);
        
        this.notify.error('A subscription error occured', null);
      }
    });

    this.userService.getUserSettings().subscribe((settings) => {
      if (this.verboseLogging){
        console.log(`${this._componentName}.ngOnInit() - userService.getUserSettings$ settings=`,settings); 
      }

      this.userSettings = settings;

      if (this.userSettings.listingViewHistory && this.userSettings.listingViewHistory.length > 0) {
        this.showRecentMenuItem = true;
      }
    });

    this.userServiceExternalOrganizationCodesSubscription = this.userService.externalOrganizationCodes.subscribe({
      next: (results) => {
        if (this.verboseLogging) {
          console.log(`${this._componentName}.ngOnInit() userService.externalOrganizationCodes results=`, results);
        }

        // TODO: Find a way to implement a configuration to filter external orgs that don't have custom home pages (e.g. Huntstand)

        if (results) {
          this.externalOrgCodes = results.filter((x) => x !== 'HS' && x !== 'DWP');
        } else {
          this.externalOrgCodes = [];
        }

        this.isAmbassador = this.externalOrgCodes.some((x) => x === 'OAA');

        this.changeDetectorRef.detectChanges();
      },
      error: (err) => {
        console.error(err);
        
        this.notify.error('A subscription error occured', null);
      }
    });

    this.userServiceActiveOrgSubscription = this.userService.activeOrg.subscribe({
      next: (v) => {
        if (this.log.verboseLogging) {
          console.log(`${this._componentName}.ngOnInit() - userService.activeOrg  subscription v=`, v);
        }

        this.activeOrgCode = v;

        if (this.activeOrgCode) {
          this.retrieveOrg(this.activeOrgCode);
        }

        this.changeDetectorRef.detectChanges();
      },
      error: (err) => {
        console.error(err);
        
        this.notify.error('A subscription error occured', null);
      }
    });

    // log in / out
    this.isLoggedInSubscription = this.userService.isLoggedIn$.subscribe((isloggedIn) => {
      this.isLoggedIn = isloggedIn; // Update reactive property
      if (!isloggedIn) {
        this.credits = null;
        this.externalOrg = null;
        this.externalOrgCodes = [];
        this.activeOrgCode = null;
        this.malibuUrl = undefined;
      } else {
        const account = this.msalService.instance.getActiveAccount();
        if (account) {
          this.msalService.instance.acquireTokenSilent({ account, scopes: this.clientConfig.apiScopes }).then(result => {
            this.malibuUrl = environment.malibuUrl + '?token=' + result.accessToken;
            this.changeDetectorRef.markForCheck();
          });
        }
        // Load admin notifications only once login is confirmed (isAdmin now
        // requires a live session), so admins still get them after the async
        // token confirmation without firing the admin-only call while logged out.
        this.loadAdminNotifications();
      }
      this.getListingCount();
    });

    // refresh
    this.refreshSubscription = this.userService.refresh$.subscribe(() => {
      this.getListingCount();
    });

    this.availableCreditSubscription = this.userService.availableReservationCredit.subscribe((x) => {
      if (this.verboseLogging) {
        console.log(`${this._componentName}.ngOnInit() userService.availableReservationCredit$ subscribe`, x);
      }
      this.credits = x;
      if (this.verboseLogging){
        console.log(`${this._componentName}.ngOnInit() - userService.availableReservationCredit$  credits=`,  this.credits); 
      }
      this.changeDetectorRef.detectChanges();
    });

    this.userService.refreshUserCredits();

    // listing deleted
    this.deletedSubscription = this.listingService.deleted$.subscribe(() => this.getListingCount());

    // listing added
    this.addedSubscription = this.listingService.added$.subscribe(() => this.getListingCount());

    this.getListingCount();

  }

  /**
   * Loads admin notifications. Guarded by isAdmin inside the service, which now
   * requires a confirmed live session, so this is a no-op for non-admin/logged-out
   * users. Called when login is confirmed rather than unconditionally at ngOnInit.
   */
  private loadAdminNotifications(): void {
    if (this.adminNotificationSubscription) {
      this.adminNotificationSubscription.unsubscribe();
    }

    this.adminNotificationSubscription = this.systemService.retrieveAdminNotifications().subscribe({
      next: (results) => {
        if (this.log.verboseLogging) {
          console.log(`${this._componentName} - systemService.retrieveAdminNotifications - subscription results=`, results);
        }

        this.adminNotifications = results;
      },
      error: (err) => {
        console.error(err);

        this.notify.error('An subscription error occured', null);
      }
    });
  }

  private retrieveOrg(code: string) {
    if (!code) {
      this.externalOrg = null;
    }

    this.externalOrgService.getExternalOrgByCode(code).subscribe({
      next: (m) => {
        this.externalOrg = m;
      },
      error: (err) => {
        console.error(err);
        
        this.notify.error('A subscription error occured', null);
      }
    });
  }

  public get showClearActiveOrgButton(): boolean {
    if (this.activeOrgCode) {
      // console.log(`${this._componentName}.showClearActiveOrgButton get - return true because route path contains "eoh" and activeOrg=`,this.activeOrg);
      return true;
    } else {
      // console.log(`${this._componentName}.showClearActiveOrgButton get - return false no activeOrg value set`);
      return false;
    }

    // console.log(`${this._componentName}.showClearActiveOrgButton get - return false because route path DOES NOT CONTAIN "eoh".  isExternalOrgHomePage=`,this.isExternalOrgHomePage);
    return false;
  }

  public get showSearchListingButton(): boolean {
    if (this.activeOrgCode) {
      return false;
    }

    return true;
  }

  public get showOrganizationMenu(): boolean {
    // console.log(`${this._componentName}.showOrganizationMenu get`);

    if (!this.isLoggedIn) {
      return false;
    }

    if (this.activeOrgCode) {
      // console.log(`${this._componentName}.showOrganizationMenu get - return false because activeOrg = ${this.activeOrg}`);
      return false;
    }

    if (this.externalOrgCodes && this.externalOrgCodes.length > 1) {
      // console.log(`${this._componentName}.showOrganizationMenu get - return true because user belongs to external orgs.`, this.externalOrgCodes);
      return true;
    }
    // console.log(`${this._componentName}.showOrganizationMenu get - return false. No condition met.`);

    return false;
  }

  public get showOrganizationButton(): boolean {
    if (this.verboseLogging) {
      console.log(`${this._componentName}.showOrganizationMenu get`);
    }

    if (!this.isLoggedIn) {
      return false;
    }

    if (this.activeOrgCode) {
      if (this.verboseLogging) {
        console.log(`${this._componentName}.showOrganizationMenu get - return false because activeOrg = ${this.activeOrgCode}`);
      }
      return false;
    }

    if (this.externalOrgCodes && this.externalOrgCodes.length == 1 && this.externalOrgDescription) {
      if (this.verboseLogging) {
        console.log(
          `${this._componentName}.showOrganizationMenu get - return true because user belongs to external orgs.`,
          this.externalOrgCodes
        );
      }
      return true;
    }

    if (this.externalOrgDescription) {
      return true;
    }
    if (this.verboseLogging) {
      console.log(`${this._componentName}.showOrganizationMenu get - return false. No condition met.`);
    }

    return false;
  }

  public onAppHeaderClick(evt: any) {
    if (this.log.verboseLogging) {
      console.log(`${this._componentName}.onAppHeaderClick  activeOrgCode=`, this.activeOrgCode);
    }
  }

  public onCreditLinkClick() {
    let trackedEvent: TrackedEvent = {} as TrackedEvent;

    let jsonData = { action: 'Credit Link Click' };

    trackedEvent.tierName = 'Presentation';
    trackedEvent.component = 'AppHeaderComponent';
    trackedEvent.category = 'ClickAction';
    trackedEvent.jsonData = JSON.stringify(jsonData);

    this.log.addTrackedEvent(trackedEvent).subscribe();

    this.notify.success(this.availableCreditMessage);
  }

  private get availableCreditMessage(): string {
    if (this.credits && this.credits.balance && this.credits.balance) {
      return `Reminder: You have $${this.credits.balance} in credits to apply to a reservation.`;
    }

    return null;
  }

  /**
   * Shows Credit in the App
   */
  public get showCreditMenuItem(): boolean {
    if (this.credits && this.credits.balance && this.credits.balance > 0) {
      if (this.log.verboseLogging) {
        console.log('showCreditMenuItem return true');
      }
      return true;
    }

    return false;
  }

  getListingCount() {
    // console.log('AppHeaderComponent: getListingCount()');
    // console.log('AppHeaderComponent" userService.isAdmin = ' + this.userService.isAdmin)
    if (!this.isLoggedIn) {
      this.hasListing = false;
      this.changeDetectorRef.markForCheck();
    } else {
      this.listingService.getCount().subscribe((c) => {
        if (c !== undefined && c > 0) {
          this.hasListing = true;
        }
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  ngOnDestroy() {
    if (this.ambassadorHomeEnabledSubscription) {
      this.ambassadorHomeEnabledSubscription.unsubscribe();
    }

    if (this.isLoggedInSubscription) {
      this.isLoggedInSubscription.unsubscribe();
    }

    if (this.userServiceExternalOrganizationCodesSubscription) {
      this.userServiceExternalOrganizationCodesSubscription.unsubscribe();
    }

    if (this.userServiceActiveOrgSubscription) {
      this.userServiceActiveOrgSubscription.unsubscribe();
    }

    if (this.routerEventsSub) {
      this.routerEventsSub.unsubscribe();
    }

    if (this.adminNotificationSubscription) {
      this.adminNotificationSubscription.unsubscribe();
    }

    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
    if (this.deletedSubscription) {
      this.deletedSubscription.unsubscribe();
    }
    if (this.addedSubscription) {
      this.addedSubscription.unsubscribe();
    }

    if (this.creditsSubscription) {
      this.creditsSubscription.unsubscribe();
    }

    if (this.availableCreditSubscription) {
      this.availableCreditSubscription.unsubscribe();
    }

    this.unSubSub$.next(void 0);
    this.unSubSub$.complete();
  }

  logOut(): void {
    this.userService.signout();
    this.hasListing = false;
    this.credits = null;
    this.router.navigate(['/']);
  }

  /**
   * Send Location to OA
   */
  public onSendLocationClick() {
    const warningMessage = 'Your location could not be determined. Check your device settings to verify Location services are enabled.';

    if (this.canGeolocate) {
      let options = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('position=', position);

          if (position) {
            let p = {
              accuracy: position.coords.accuracy,
              altitude: position.coords.altitude,
              altitudeAccuracy: position.coords.altitudeAccuracy,
              heading: position.coords.heading,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              speed: position.coords.speed,
              timestamp: position.timestamp,
            };

            this.userService.sendPosition(p).subscribe({
              next: () => {
                this.notify.success('Position Successfully Sent!');
              },
              error: (err) => {
                console.error(err);
                this.notify.error('An error ocurred while sending your position to Outdoor Access.', null);
              }
            });
          } else {
            this.notify.warning(warningMessage);
          }
        },
        (err) => {
          console.error(err);
          this.notify.warning(warningMessage);
        },
        options
      );
    } else {
      this.notify.warning(warningMessage);
    }
  }

  /**
   * Boolean that controls whether the Send OA My Location button is shown
   */
  public get showSendLocation(): boolean {
    return false;
  }

  /**
   * Property indicating if the browser can obtain geolocate position
   */
  public get canGeolocate(): boolean {
    if (navigator.geolocation) {
      return true;
    } else {
      return false;
    }
  }

  public get activeOrgCode(): string {
    return this._activeOrgCode;
  }
  public set activeOrgCode(v: string) {
    this._activeOrgCode = v;
    if (this.verboseLogging){
      console.log(`${this._componentName}.activeOrgCode set v=`, v);
    }    
  }

  public onAdminNotificatonClick(evt: any) {
    console.log(`${this._componentName}.onAdminNotificatonClick()`);
    evt.stopPropagation();

    this.router.navigate(['/admin/notify']);
  }

  public clearActiveOrg() {
    this.userService.clearActiveOrg();

    // Take us home
    this.router.navigate(['/']);
  }

  public get isImpersonating(): boolean {
    if (this.userService && this.userService.isImpersonating) {
      return true;
    } else {
      return false;
    }
  }

  public get externalOrgDescription(): string {
    if (this.externalOrg && this.externalOrg.description) {
      return this.externalOrg.description;
    }

    return undefined;
  }

  public onOrgButtonClick() {
    console.log(`${this._componentName}.onOrgButtonClick()`);

    this.activeOrgCode = this.externalOrg.code;

    this.userService.setActiveOrg(this.externalOrg.code);

    this.routeHelper.goHome(this.activeOrgCode);
  }

  public onHeaderLogoClick() {
    console.log(`${this._componentName}.onHeaderLogoClick()`);

    this.routeHelper.goHome(this.activeOrgCode);
    window.location.href = environment.nextJsUrl;
  }


  login() {
    this.loginEvent.emit();
  }

  logout() {
    this.logoutEvent.emit();
  }

}
